/**
 * @file schemas/certified-org-directory.ts
 * @description 獲證組織名錄 Zod 校驗 schema。
 *
 * 注意：日期欄位使用 z.iso.date()（Zod v4 的 ISO 8601 date-only 校驗），
 * 而非 z.string().date()（Zod v3 語法，v4 已不支援）。
 */
import { z } from 'zod';
import { DIRECTORY_CATEGORIES, DIRECTORY_STATUSES } from '../utils/directory-category';
export const directoryCategorySchema = z.enum(DIRECTORY_CATEGORIES);
export const directoryStatusSchema = z.enum(DIRECTORY_STATUSES);
/** 名錄驗證類別（個別/集團）— 供前端 Select 與後端 schema 共用。 */
export const DIRECTORY_VERIFICATION_TYPES = ['個別驗證', '集團驗證'];
/** 有機/轉型期 — 供前端 Select 與後端 schema 共用。 */
export const ORGANIC_STAGES = ['有機', '有機轉型期'];
/** 驗證項目子項（類別 + 細項清單） */
export const verificationItemSchema = z.object({
    category: z.string().min(1),
    items: z.array(z.string()),
});
/** 建立名錄記錄的輸入 schema */
export const createDirectorySchema = z.object({
    // 必填
    category: directoryCategorySchema,
    clientId: z.number().int().positive(),
    registrationNo: z.string().min(1).max(50),
    // 選填
    serialNo: z.number().int().nonnegative().optional(),
    certificateNo: z.string().max(50).optional(),
    certificateId: z.number().int().positive().optional(),
    // 受評者快照（未傳則 service 從 clients prefill）
    legalRepresentative: z.string().max(100).optional(),
    registeredAddress: z.string().optional(),
    mailingAddress: z.string().optional(),
    postalCode: z.string().max(10).optional(),
    shippingAddress: z.string().optional(),
    phone: z.string().max(30).optional(),
    // 驗證資訊
    verificationStandard: z.string().optional(),
    verificationBehavior: z.string().max(30).optional(),
    verificationType: z.enum(DIRECTORY_VERIFICATION_TYPES).optional(),
    verificationOption: z.string().max(50).optional(),
    verificationScope: z.string().optional(),
    verificationItems: z.array(verificationItemSchema).optional(),
    area: z.number().nonnegative().optional(),
    memberCount: z.number().int().nonnegative().optional(),
    organicStage: z.enum(ORGANIC_STAGES).optional(),
    /** 已完成驗證年度（如「2024、2025」；sync 自動彙總，admin 可修正） */
    completedYears: z.string().max(200).optional(),
    /** 已完成驗證類型（如「初次申請、追蹤查驗1」） */
    completedTypes: z.string().max(200).optional(),
    // 發證/期限（ISO 8601 date-only，格式 YYYY-MM-DD）
    firstIssuedAt: z.iso.date().optional(),
    latestIssuedAt: z.iso.date().optional(),
    validFrom: z.iso.date().optional(),
    validUntil: z.iso.date().optional(),
    effectiveFrom: z.iso.date().optional(),
    // 進度
    currentYear: z
        .string()
        .regex(/^\d{4}$/)
        .optional(),
    currentStatus: z.string().max(20).optional(),
    verificationActionNote: z.string().optional(),
    // 派案
    auditorY1: z.string().max(100).optional(),
    auditorY2: z.string().max(100).optional(),
    auditorY3: z.string().max(100).optional(),
    auditorY4: z.string().max(100).optional(),
    auditorYearBase: z.number().int().min(2000).max(2100).optional(),
    plannedAuditorNext: z.string().max(100).optional(),
    auditorAvoidance: z.string().optional(),
    dispatchNote: z.string().optional(),
    /** 稽核組連任 3 年預警（正常由 sync 計算；admin 後門可修正） */
    rotationWarning: z.boolean().optional(),
    // 業務備註
    channels: z.string().optional(),
    primaryLandNo: z.string().optional(),
    certificateChangeNote: z.string().optional(),
    terminationDocStatus: z.string().max(20).optional(),
    terminationTrackingNote: z.string().optional(),
    paymentWarning: z.boolean().optional(),
    /** 待停止/結束狀態 */
    pendingTermination: z.boolean().optional(),
    /** 輔導員（姓名[/電話] 快照） */
    counselor: z.string().max(100).optional(),
    /** 紙本報告是否已歸檔 */
    paperReportArchived: z.boolean().optional(),
    /** 驗證組備註 */
    verificationGroupNote: z.string().optional(),
    /** 客戶備註 */
    clientNote: z.string().optional(),
    /** 管理組備註 */
    adminGroupNote: z.string().optional(),
    /** 驗證場所地址（主要地號所在地址） */
    mainParcelAddress: z.string().optional(),
    /** 採收後處理場所（[{ name, address, operations }]） */
    processingSites: z
        .array(z.object({
        name: z.string().nullable().optional(),
        address: z.string().nullable().optional(),
        operations: z.unknown().optional(),
    }))
        .optional(),
    // 類別專屬
    environmentalSubsidyMonth: z.string().max(30).optional(),
    l1Uploaded: z.boolean().optional(),
    planNo: z.string().max(30).optional(),
    industryType: z.string().max(50).optional(),
    tapCode: z.string().max(50).optional(),
    labelUsage: z.string().optional(),
    /** TAP 專屬：產品類別 */
    productCategory: z.string().max(100).optional(),
    /** TAP 專屬：產品品項 */
    productItems: z.string().optional(),
    /** TOP 專屬：驗證品項 */
    verifiedItems: z.string().optional(),
    // 狀態
    status: directoryStatusSchema.optional(),
    isActive: z.boolean().optional(),
});
/** 更新名錄記錄的輸入 schema（所有欄位皆可選） */
export const updateDirectorySchema = createDirectorySchema.partial();
/** query 布林參數（'true'/'false' 字串 → boolean） */
const queryBooleanSchema = z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional();
/**
 * 名錄列表查詢 schema。
 *
 * 除全域 search 外，另支援表頭篩選（Excel autofilter 式）所需的
 * 逐欄過濾參數：低基數欄用等值 / 布林過濾，文字欄用 ilike 模糊搜尋。
 */
export const directoryListQuerySchema = z.object({
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(20),
    category: directoryCategorySchema.optional(),
    status: directoryStatusSchema.optional(),
    clientId: z.coerce.number().int().positive().optional(),
    search: z.string().optional(),
    validUntilBefore: z.iso.date().optional(),
    paymentWarning: queryBooleanSchema,
    // ── 表頭篩選：低基數欄（等值 / 布林） ──
    /** 稽核組連任 3 年預警 */
    rotationWarning: queryBooleanSchema,
    /** 待停止/結束狀態 */
    pendingTermination: queryBooleanSchema,
    /** 驗證類別（個別驗證/集團驗證） */
    verificationType: z.string().max(20).optional(),
    /** 有機/轉型期 */
    organicStage: z.string().max(20).optional(),
    // ── 表頭篩選：文字欄（ilike 模糊） ──
    /** 驗證註冊號 */
    registrationNo: z.string().max(50).optional(),
    /** 證書編號 */
    certificateNo: z.string().max(50).optional(),
    /** 受評者名稱 */
    clientName: z.string().max(200).optional(),
    /** 法定負責人 */
    legalRepresentative: z.string().max(100).optional(),
    /** 輔導員 */
    counselor: z.string().max(100).optional(),
    /** 進度文字標籤（初次/追查/展延/稽核…） */
    currentStatus: z.string().max(20).optional(),
});
